import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import type {
  BaseEntity,
  Resource,
  Role,
  UserType,
} from '~settings/api/lib/schemas';
import type { DEAL_REGION_SCOPE } from '~settings/constants';

import type { PageParam } from './paginationOptions';

export enum HttpStatusCode {
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  InternalServerError = 500,
  EntityConflict = 409,
}

export type PresetUseQueryOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, HTTPError, TData>,
  'queryKey' | 'queryFn'
>;

export type PresetUseMutationOptions<
  TMutationFnData,
  TData = TMutationFnData,
> = Omit<UseMutationOptions<TMutationFnData, HTTPError, TData>, 'mutationFn'>;

export type PresetUseInfiniteQueryOptions<
  TQueryFnData,
  TData = InfiniteData<TQueryFnData>,
  TSelectData = TData,
> = Omit<
  UseInfiniteQueryOptions<
    TQueryFnData,
    HTTPError,
    TData,
    TQueryFnData,
    QueryKey,
    PageParam
  >,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam' | 'select'
> & {
  select?: (data: InfiniteData<TQueryFnData>) => TSelectData;
};

export interface UserDetailPayload {
  firstName: string;
  lastName: string;
  isInternal?: boolean;
  userType?: UserType;
  email: string;
  phone: string | null;
  jobTitle?: string;
  organization: string;
  isActive?: boolean;
  rolesMap?: Record<
    Role,
    {
      labels: BaseEntity[];
      labelGroups: BaseEntity[];
    }
  >;
  roleResources?: Partial<Record<Role, Resource[]>>;
}

export interface LabelDetailPayload {
  active: boolean;
  allowTimedRelease: boolean;
  asylum: boolean;
  dealId: string;
  dealOwner: string;
  dealRegionScope: DEAL_REGION_SCOPE;
  directPassQc: boolean;
  distributionGroups: {
    id?: string;
    name: string;
    type: DISTRIBUTION_TERRITORIES;
    notes?: string | null;
    countries: string[];
  }[];
  distributorId: string;
  facebookReference: string | null;
  gpiNumber: string;
  gvlCode: string;
  instagramReference: string | null;
  labelGroups: string[];
  labelIsrcPrefix: string | null;
  labelPresentation: string;
  marketingCompany: string;
  name: string;
  parentId: string | null;
  pmo: string;
  profitCenter: string;
  repertoireOwner: string;
  scopeId: string;
  twitterReference: string | null;
  usLabelCode: string;
  wbsElementNumber: string;
  weaLabelCode: string;
  website: string | null;
  wmiLabelCode: string;
  youtubeReference: string | null;
  digitalProduct: boolean;
  physicalProduct: boolean;
}

export interface LabelGroupPayload {
  name: string;
  notes: string | undefined;
  labelIds: string[];
}

export interface SublabelRequestPayload {
  name: string;
  notes?: string;
  parentId: string;
}

export enum DISTRIBUTION_TERRITORIES {
  Worldwide = 'WORLDWIDE',
  OnlyIn = 'ONLY_IN',
  WorldwideExclude = 'WORLDWIDE_EXCLUDE',
}
